import * as React from "react";
import {
  create as EasepickCore,
  DateTime,
  TimePlugin,
} from "@easepick/bundle";
import type { Core } from "@easepick/core";

interface DateTimeInputProps {
  name: string;
  value?: string;
  defaultValue?: string;
  inputClassName?: string;
  placeholder?: string;
  disabled?: boolean;
  dataTestId?: string;
  onChange?: (isoValue: string) => void;
}

interface DateTimeInputState {
  internalValue: string;
}

const DISPLAY_FORMAT = "YYYY-MM-DD HH:mm";

export class DateTimeInput extends React.Component<DateTimeInputProps, DateTimeInputState> {
  private picker: Core | null = null;
  private hiddenInput: HTMLInputElement | null = null;
  private displayInput: HTMLInputElement | null = null;

  constructor(props: DateTimeInputProps) {
    super(props);
    this.state = {
      internalValue: props.value ?? props.defaultValue ?? "",
    };
  }

  componentDidMount(): void {
    this.syncInputs(this.getCurrentValue());
    this.initializePicker();
  }

  componentDidUpdate(prevProps: DateTimeInputProps): void {
    // Only sync from props if we're in controlled mode (value prop is used)
    if (this.props.value !== undefined && prevProps.value !== this.props.value) {
      this.setState({ internalValue: this.props.value });
      this.syncInputs(this.props.value, true);
    }
  }

  componentWillUnmount(): void {
    if (this.picker) {
      this.picker.destroy();
      this.picker = null;
    }
  }

  private getCurrentValue(): string {
    // If controlled (value prop), use that; otherwise use internal state
    return this.props.value ?? this.state.internalValue;
  }

  private initializePicker(): void {
    if (!this.displayInput) {
      return;
    }

    const initialIso = this.getCurrentValue();

    this.picker = new EasepickCore({
      element: this.displayInput,
      css: ["https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css"],
      format: DISPLAY_FORMAT,
      autoApply: true,
      plugins: [TimePlugin],
      TimePlugin: {
        stepMinutes: 1,
        seconds: false,
      },
      setup: (picker) => {
        if (initialIso) {
          picker.setDate(new Date(initialIso));
        }
      },
    });

    // Listen for both "select" (day click) and "change" (time dropdown change)
    // See: https://github.com/easepick/easepick/issues/209
    const handlePickerChange = () => {
      const date = this.picker?.getDate();
      const iso = date ? date.toISOString() : "";
      this.handleDateSelect(iso);
    };

    this.picker.on("select", handlePickerChange);
    this.picker.on("change", handlePickerChange);
  }

  private handleDateSelect(iso: string): void {
    // Update internal state FIRST, before calling onChange
    // This ensures the value persists through any re-render triggered by onChange
    this.setState({ internalValue: iso }, () => {
      // After state is committed, sync the DOM inputs
      this.syncInputs(iso);
      // Then notify parent
      this.props.onChange?.(iso);
    });
  }

  private syncInputs(iso: string, updatePicker = false): void {
    if (this.hiddenInput) {
      this.hiddenInput.value = iso || "";
    }

    if (this.displayInput) {
      this.displayInput.value = iso ? this.formatDisplayValue(iso) : "";
    }

    if (updatePicker && this.picker) {
      if (iso) {
        this.picker.setDate(new Date(iso));
      } else {
        this.picker.clear();
      }
    }
  }

  private formatDisplayValue(iso: string): string {
    const parsedDate = new Date(iso);

    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    const formatted = new DateTime(parsedDate);
    return formatted.format(DISPLAY_FORMAT);
  }

  private setHiddenRef = (el: HTMLInputElement | null) => {
    this.hiddenInput = el;
  };

  private setDisplayRef = (el: HTMLInputElement | null) => {
    this.displayInput = el;
  };

  render(): JSX.Element {
    const currentValue = this.getCurrentValue();

    return (
      <>
        <input
          type="hidden"
          name={this.props.name}
          value={currentValue}
          onChange={() => {}} // Required for controlled input
          ref={this.setHiddenRef}
          data-testid={
            this.props.dataTestId ? `${this.props.dataTestId}-hidden` : undefined
          }
        />

        <input
          type="text"
          className={this.props.inputClassName}
          placeholder={this.props.placeholder || "YYYY-MM-DD HH:mm"}
          defaultValue={currentValue ? this.formatDisplayValue(currentValue) : ""}
          ref={this.setDisplayRef}
          readOnly
          disabled={this.props.disabled}
          data-testid={
            this.props.dataTestId
              ? `${this.props.dataTestId}-display`
              : undefined
          }
        />
      </>
    );
  }
}
